/**
 * DS-internal hook : la molette relayée, au web. Au grand écran du live, la visio se tient à côté de l'énoncé,
 * qui seul défile ; la molette au-dessus de la visio ne faisait rien, et l'élève ne voyait pas que l'énoncé
 * avait une suite (lives des 23 et 24/10). Ici, elle fait défiler l'énoncé d'où qu'elle parte.
 *
 *  - `useWheelRelay(source, enabled)` : la zone d'où part la molette (sa vue, un élément du DOM au web) ;
 *    il rend de quoi s'inscrire, que la page pose dans `WheelRelayContext` autour de ce qui défile ;
 *  - ce qui défile s'y inscrit (`useWheelRelayTarget`, l'énoncé de `<StudentActivityPage>`), et s'en retire en
 *    partant. Rien d'inscrit (une activité faite relue, le quiz) : la molette ne fait rien, comme avant.
 *
 * Le zoom (Ctrl + molette, le pincement d'un pavé tactile) n'est pas relayé. La zone ne défile pas elle-même :
 * l'écouteur reste passif, rien n'est empêché. Au natif, rien : pas de molette.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef } from "react"
import { Platform } from "react-native"

/** Ce qui défile, au web : l'élément du DOM d'un `<ScrollView>` (`getScrollableNode`). */
type WheelTarget = { scrollBy: (options: { top: number }) => void; clientHeight: number }
type WheelEventLike = { deltaY: number; deltaMode: number; ctrlKey: boolean }
type WheelSource = {
    addEventListener: (type: "wheel", listener: (event: WheelEventLike) => void, options: { passive: true }) => void
    removeEventListener: (type: "wheel", listener: (event: WheelEventLike) => void) => void
}

type Register = (target: WheelTarget | null) => () => void

export const WheelRelayContext = createContext<Register | null>(null)

/** Une ligne de molette, en pixels : Firefox compte en lignes. */
const LINE_PX = 16

/** Le défilement d'un cran de molette, en pixels (en lignes ou en pages, suivant le navigateur) ; `pageSize` est la
 * taille d'une page dans le sens où l'on défile. */
export function wheelPixels(event: Pick<WheelEventLike, "deltaY" | "deltaMode">, pageSize: number): number {
    if (event.deltaMode === 1) return event.deltaY * LINE_PX
    if (event.deltaMode === 2) return event.deltaY * pageSize
    return event.deltaY
}

export function useWheelRelay(source: React.RefObject<unknown>, enabled: boolean): Register {
    const target = useRef<WheelTarget | null>(null)
    const register = useCallback<Register>((node) => {
        target.current = node
        return () => {
            if (target.current === node) target.current = null
        }
    }, [])

    useEffect(() => {
        const node = source.current as Partial<WheelSource> | null
        if (Platform.OS !== "web" || !enabled || typeof node?.addEventListener !== "function") return undefined
        const onWheel = (event: WheelEventLike) => {
            const to = target.current
            if (to == null || event.ctrlKey || event.deltaY === 0) return
            to.scrollBy({ top: wheelPixels(event, to.clientHeight) })
        }
        node.addEventListener("wheel", onWheel, { passive: true })
        return () => node.removeEventListener?.("wheel", onWheel)
    }, [source, enabled])

    return register
}

/** Ce qui défile s'inscrit auprès de la page qui relaie la molette, s'il y en a une (le web seulement). */
export function useWheelRelayTarget(scroller: React.RefObject<unknown>): void {
    const register = useContext(WheelRelayContext)
    useEffect(() => {
        if (register == null || Platform.OS !== "web") return undefined
        const node = (scroller.current as { getScrollableNode?: () => unknown } | null)?.getScrollableNode?.()
        if (node == null || typeof (node as Partial<WheelTarget>).scrollBy !== "function") return undefined
        return register(node as WheelTarget)
    }, [register, scroller])
}
